/** @jsx jsx */
import { jsx } from '@emotion/core'
import { FunctionComponent, useState } from 'react'

import { IUserCredentials } from '~/types'
import { Input, Select, Checkbox } from '~/components'
import { STATES } from '~/config'
import { styles } from './styles'
import { Message } from '../message'

interface FormProps {
  onRegisterWithCredentials: (credentials: IUserCredentials) => any
  onLoginWithCredentials: (credentials: IUserCredentials) => any
  onLoginWithGoogle: () => any
  onLoginWithFacebook: () => any
  onClickCreateAccount: () => any
  onClickSignIn: () => any
  mode: 'create-account' | 'sign-in'
  loading?: boolean
  error?: Error
  message?: string
}

export const AuthForm: FunctionComponent<FormProps> = ({
  onRegisterWithCredentials,
  onLoginWithCredentials,
  onLoginWithGoogle,
  onLoginWithFacebook,
  onClickCreateAccount,
  onClickSignIn,
  mode = 'create-account',
  loading = false,
  error = null,
  message = null,
}) => {
  const [credentials, setCredentials] = useState<IUserCredentials>({
    email: '',
    password: '',
    role: '',
  })

  const [agreedToTerms, setAgreedToTerms] = useState(true)
  const [subscribedToUpdates, setSubscribedToUpdates] = useState(true)

  return (
    <form
      css={styles.container}
      onSubmit={(e) => {
        e.preventDefault()

        if (mode === 'sign-in') {
          onLoginWithCredentials(credentials)
        } else {
          onRegisterWithCredentials(credentials)
        }
      }}
    >
      <div css={styles.navigation.container}>
        <h3
          css={[
            styles.navigation.item,
            mode === 'create-account' && styles.navigation.active,
          ]}
          onClick={onClickCreateAccount}
          data-test-id="set-mode-create-account"
          tabIndex={1}
        >
          CREATE ACCOUNT
        </h3>

        <h3
          css={[styles.navigation.item, mode === 'sign-in' && styles.navigation.active]}
          onClick={onClickSignIn}
          data-test-id="set-mode-log-in"
          tabIndex={2}
        >
          SIGN IN
        </h3>
      </div>

      <Input
        name="email"
        placeholder="Email"
        type="email"
        autoComplete="email"
        required
        tabIndex={3}
        value={credentials.email}
        onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
        disabled={loading}
      />

      <Input
        name="password"
        placeholder="Password"
        type="password"
        autoComplete="password"
        required
        tabIndex={4}
        value={credentials.password}
        onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
        disabled={loading}
      />

      {mode === 'create-account' && (
        <Select
          name="role"
          placeholder="What are you looking for?"
          required
          selected={credentials.role || ''}
          options={[
            { name: "I'm looking for parts", value: 'customer' },
            { name: 'I want to sell parts', value: 'dealer' },
          ]}
          onChange={(selected: 'customer' | 'dealer') =>
            setCredentials({ ...credentials, role: selected })
          }
        />
      )}

      {mode === 'create-account' && (
        <div css={styles.checkboxes}>
          <Checkbox
            name="terms"
            type="checkbox"
            tabIndex={6}
            disabled={loading}
            checked={agreedToTerms}
            onChange={(checked) => setAgreedToTerms(checked)}
            label={
              <div>
                {' '}
                Agree to{' '}
                <a href="#" css={styles.link}>
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="#" css={styles.link}>
                  Terms of Service
                </a>
              </div>
            }
          />

          <Checkbox
            name="updates"
            type="checkbox"
            tabIndex={7}
            disabled={loading}
            checked={subscribedToUpdates}
            onChange={(checked) => setSubscribedToUpdates(checked)}
            label={'Subscribe to updates and promotional meterials'}
          />
        </div>
      )}

      {message && <Message type="info">{message}</Message>}

      {error && <Message type="error">{error.message}</Message>}

      <button
        css={styles.button}
        name="submit"
        type="submit"
        tabIndex={8}
        disabled={!agreedToTerms || loading}
      >
        {mode === 'create-account' ? 'CREATE ACCOUNT' : 'SIGN IN'}
      </button>

      <div
        css={[
          styles.social.container,
          (!agreedToTerms || loading) && styles.social.disabled,
        ]}
      >
        <div
          css={styles.social.section}
          onClick={onLoginWithGoogle}
          tabIndex={9}
          data-test-id="continue-with-google"
        >
          <img
            src={require('~/assets/google.svg')}
            width="16px"
            height="16px"
            alt="google icon"
          />
          <span>Continue with Google</span>
        </div>

        <div
          css={styles.social.section}
          onClick={onLoginWithFacebook}
          tabIndex={10}
          data-test-id="continue-with-facebook"
        >
          <img
            src={require('~/assets/facebook.svg')}
            width="16px"
            height="16px"
            alt="facebook icon"
          />
          <span>Continue with Facebook</span>
        </div>
      </div>
    </form>
  )
}
